import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/context/SiteContext';
import { cn } from '@/lib/cn';

/**
 * Brand mark.
 *
 * The source site's logo file is copyrighted and was not copied, so this renders an original
 * typographic wordmark with a small geometric glyph. Uploading a real logo in the CMS
 * (Settings → Identity) replaces it automatically — no code change needed.
 */
export function Logo({ inverse, className }: { inverse?: boolean; className?: string }) {
  const settings = useSiteSettings();
  const asset = inverse ? (settings.logo_dark ?? settings.logo) : settings.logo;

  return (
    <Link
      to="/"
      className={cn('group inline-flex items-center gap-2.5 no-underline hover:no-underline', className)}
      aria-label={`${settings.company_name} — home`}
    >
      {asset?.url ? (
        <img src={asset.url} alt={asset.alt_text || settings.company_name} className="h-9 w-auto" />
      ) : (
        <>
          <span aria-hidden="true" className="relative block h-8 w-8 shrink-0">
            <svg viewBox="0 0 32 32" className="h-8 w-8" role="presentation">
              <defs>
                <linearGradient id="shyena-mark" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary)" />
                  <stop offset="100%" stopColor="var(--color-accent)" />
                </linearGradient>
              </defs>
              <rect width="32" height="32" rx="8" fill="url(#shyena-mark)" />
              <path d="M9 21.5 16 8l7 13.5h-4.2L16 16.2l-2.8 5.3z" fill="white" fillOpacity="0.95" />
              <circle cx="16" cy="24" r="1.6" fill="white" fillOpacity="0.75" />
            </svg>
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={cn(
                'font-display text-[1.0625rem] font-bold tracking-tight',
                inverse ? 'text-white' : 'text-ink',
              )}
            >
              {settings.company_short_name || 'Shyena'}
            </span>
            <span className={cn('mt-0.5 text-[0.625rem] uppercase tracking-[0.18em]', inverse ? 'text-white/60' : 'text-muted')}>
              Solutions
            </span>
          </span>
        </>
      )}
    </Link>
  );
}
